import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Keep a scrolling transcript pinned to its newest line.
 *
 * A chat that does not follow its own output leaves the reader looking at an
 * old message while the answer they asked for arrives off-screen below.
 *
 * Pinned, not forced. Scrolling up to re-read something is a deliberate act,
 * and yanking the view back on the next chunk of a streaming reply would make
 * the history unreadable while the agent is talking. So the hook follows only
 * while the reader is already at the bottom, and lets go the moment they
 * scroll away — returning on its own once they come back down.
 *
 * `deps` is what counts as new output. A streaming reply mutates the last
 * message rather than adding one, so passing only the message count would
 * follow the first chunk and none of the rest.
 *
 * Returns `[ref, pin]`. `pin` overrides the reader's position and is for the
 * one case where that is right: they just sent something, so they want to see
 * it whatever they were reading a moment ago.
 */
export function useStickyBottom(deps, { enabled = true } = {}) {
  const ref = useRef(null);
  /* Starts true so the first paint lands at the bottom rather than the top. */
  const stuck = useRef(true);

  /** Within a line's height of the end counts as "at the bottom". */
  const atBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < 24;

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (el) stuck.current = atBottom(el);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', onScroll, { passive: true });

    /**
     * Re-pin whenever the box or its content changes size.
     *
     * The dock's panel opens on a 300ms max-height transition, so at the
     * moment a render says "a message arrived" the panel is still collapsed
     * and there is nothing to scroll — the effect below sets scrollTop on a
     * zero-height box and it stays at the top. Watching the size catches the
     * end of that animation, and equally a reply that reflows as it wraps.
     */
    if (typeof ResizeObserver === 'undefined') {
      return () => el.removeEventListener('scroll', onScroll);
    }
    const pin = () => {
      if (stuck.current) el.scrollTop = el.scrollHeight;
    };
    const ro = new ResizeObserver(pin);
    ro.observe(el);
    /* The content grows independently of the box it sits in, and it is the
       newest line that grows — while it streams in, and while a finished reply
       is being written out. Re-attached on every new line so the observer is
       always watching the one that is moving, not the one it saw first. */
    [el.firstElementChild, el.lastElementChild].forEach((c) => c && ro.observe(c));
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
    /* Same list as the pin below: the caller's definition of new output. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onScroll, ...deps]);

  /* Layout, not effect: this runs before the browser paints, so the transcript
     never shows the old position for a frame and then jumps. */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !enabled || !stuck.current) return;
    el.scrollTop = el.scrollHeight;
    /* The dependency list is the caller's definition of new output. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  /**
   * Follow again from here, whatever the reader had scrolled to.
   *
   * Deferred a frame: the caller sends before React has rendered the line, so
   * scrolling now would land on the height the box had a moment ago. The flag
   * is set immediately so the growth this triggers is followed too.
   */
  const pin = useCallback(() => {
    stuck.current = true;
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  return [ref, pin];
}
